/**
 * ACH capture relay encryption (DTB-263 / DTB-264 — encrypt-through-core).
 *
 * The browser RSA-OAEP-seals the bank payload to the payment-processing-api's
 * PUBLIC key, then hands the opaque blob to edutrak-core, which relays it
 * server-to-server to the payment API. core holds no private key, so it can
 * never read what it carries; only the payment API can decrypt it.
 *
 * The public key is PINNED in this build artifact (VITE_ACH_RELAY_PUBKEY) — it
 * is NOT fetched from core at runtime. That is the load-bearing property: a
 * compromised core cannot substitute its own key and MITM the payload. Rotation
 * = ship a new pinned public key in the usf build + the matching private key on
 * the payment API.
 *
 * RSA-OAEP(SHA-256) matches the payment API's `cryptography` decrypt. routing +
 * account are tiny, so direct RSA-OAEP needs no hybrid envelope. Native
 * WebCrypto only — zero new dependencies.
 */

export interface AchBankPayload {
  routing_number: string
  account_number: string
  account_type: 'CHECKING' | 'SAVING'
}

/** Decode a base64 string to a byte array (browser-native). */
const base64ToBytes = (b64: string): Uint8Array => Uint8Array.from(atob(b64), c => c.charCodeAt(0))

/** Encode a byte array to a base64 string (browser-native). */
const bytesToBase64 = (bytes: Uint8Array): string => {
  let binary = ''
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}

/** Strip the PEM armor + whitespace, leaving the base64 DER body. */
const pemToDerBase64 = (pem: string): string =>
  pem
    .replace(/-----BEGIN [^-]+-----/, '')
    .replace(/-----END [^-]+-----/, '')
    .replace(/\s+/g, '')

let importedKeyPromise: Promise<CryptoKey> | null = null

/** Import the pinned SPKI public key once, then reuse it. */
const getRelayPublicKey = (): Promise<CryptoKey> => {
  if (importedKeyPromise) {
    return importedKeyPromise
  }
  // Vite leaves literal '\n' in the env value untouched; normalize them so the
  // PEM parses whether the value used real or escaped newlines.
  const pem = (import.meta.env.VITE_ACH_RELAY_PUBKEY || '').replace(/\\n/g, '\n')
  if (!pem.trim()) {
    return Promise.reject(new Error('VITE_ACH_RELAY_PUBKEY is not configured in this build.'))
  }
  const der = base64ToBytes(pemToDerBase64(pem))
  // Clear the memo on failure. Caching a rejected promise meant a malformed PEM
  // made every subsequent attempt in the page fail with the same stale error and
  // no fresh diagnostic.
  importedKeyPromise = crypto.subtle
    .importKey('spki', der, { name: 'RSA-OAEP', hash: 'SHA-256' }, false, ['encrypt'])
    .catch(err => {
      importedKeyPromise = null
      throw err
    })
  return importedKeyPromise
}

/**
 * Seal a bank payload to the pinned relay public key. Returns the base64
 * RSA-OAEP ciphertext (`enc_payload`) to hand to core — never plaintext.
 */
export const sealAchPayload = async (payload: AchBankPayload): Promise<string> => {
  const key = await getRelayPublicKey()
  const plaintext = new TextEncoder().encode(JSON.stringify(payload))
  const ciphertext = await crypto.subtle.encrypt({ name: 'RSA-OAEP' }, key, plaintext)
  return bytesToBase64(new Uint8Array(ciphertext))
}
